import React, { useState, useEffect, useRef } from 'react';
import { ReferenceLine, ReferenceDot, ReferenceArea } from 'recharts';
import { createPortal } from 'react-dom';
import type {
  ChartReferenceLine,
  ChartReferenceDot,
  ChartReferenceArea,
} from '../../types';

// ── Defaults ──────────────────────────────────────────────

const DEFAULT_COLOR = 'hsl(var(--muted-foreground))';
const DEFAULT_DASH = '4 4';
const DEFAULT_DOT_SHAPE = 'diamond';
const DEFAULT_DOT_RADIUS = 5;
const DEFAULT_AREA_OPACITY = 0.1;
const LABEL_MAX_LENGTH = 25;

function clampLabel(text: string): string {
  return text.length > LABEL_MAX_LENGTH
    ? text.substring(0, LABEL_MAX_LENGTH - 3) + '...'
    : text;
}

// ── Shape path builders ───────────────────────────────────

function diamondPath(cx: number, cy: number, r: number): string {
  return `M ${cx} ${cy - r} L ${cx + r} ${cy} L ${cx} ${cy + r} L ${cx - r} ${cy} Z`;
}

function squarePath(cx: number, cy: number, r: number): string {
  return `M ${cx - r} ${cy - r} L ${cx + r} ${cy - r} L ${cx + r} ${cy + r} L ${cx - r} ${cy + r} Z`;
}

function trianglePath(cx: number, cy: number, r: number): string {
  const h = r * Math.sqrt(3);
  return `M ${cx} ${cy - r} L ${cx + h / 2} ${cy + r / 2} L ${cx - h / 2} ${cy + r / 2} Z`;
}

/**
 * Creates a Recharts-compatible `shape` render function for a ReferenceDot.
 * Renders a custom SVG shape (diamond, square, triangle, circle) with
 * mouse events that dispatch scoped CustomEvents for the tooltip layer.
 */
function createDotShapeRenderer(dot: ChartReferenceDot, chartId: string) {
  const color = dot.color || DEFAULT_COLOR;
  const r = dot.r ?? DEFAULT_DOT_RADIUS;
  const shapeType = dot.shape || DEFAULT_DOT_SHAPE;

  // eslint-disable-next-line react/display-name
  return (props: { cx?: number; cy?: number }) => {
    const { cx, cy } = props;
    if (cx == null || cy == null) return <g />;

    const handleMouseEnter = (e: React.MouseEvent<SVGElement>) => {
      if (!dot.description) return;
      const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
      window.dispatchEvent(
        new CustomEvent('chart-ref-dot-hover', {
          detail: { chartId, x: rect.right, y: rect.top, label: dot.label, description: dot.description },
        })
      );
    };

    const handleMouseLeave = () => {
      window.dispatchEvent(new CustomEvent('chart-ref-dot-leave', { detail: { chartId } }));
    };

    const commonProps = {
      fill: color,
      stroke: 'hsl(var(--background))',
      strokeWidth: 2,
      style: { cursor: dot.description ? 'pointer' : 'default' } as React.CSSProperties,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    };

    if (shapeType === 'circle') {
      return <circle cx={cx} cy={cy} r={r} {...commonProps} />;
    }

    let d: string;
    switch (shapeType) {
      case 'square':
        d = squarePath(cx, cy, r);
        break;
      case 'triangle':
        d = trianglePath(cx, cy, r);
        break;
      case 'diamond':
      default:
        d = diamondPath(cx, cy, r);
        break;
    }

    return <path d={d} {...commonProps} />;
  };
}

// ── Tooltip layer ─────────────────────────────────────────

interface TooltipData {
  x: number;
  y: number;
  label?: string;
  description: string;
}

/**
 * Portal-based tooltip that listens for scoped CustomEvents from ReferenceDot shapes.
 * Render this component as a **sibling** of (not inside) the Recharts chart.
 */
export const ReferenceDotTooltipLayer: React.FC<{ chartId: string }> = ({ chartId }) => {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onHover = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail.chartId !== chartId) return;
      setTooltip({ x: detail.x, y: detail.y, label: detail.label, description: detail.description });
    };
    const onLeave = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail.chartId !== chartId) return;
      setTooltip(null);
    };

    window.addEventListener('chart-ref-dot-hover', onHover);
    window.addEventListener('chart-ref-dot-leave', onLeave);
    return () => {
      window.removeEventListener('chart-ref-dot-hover', onHover);
      window.removeEventListener('chart-ref-dot-leave', onLeave);
    };
  }, [chartId]);

  if (!tooltip) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      className="pointer-events-none fixed z-50 max-w-xs rounded-lg border bg-background p-2 shadow-md text-sm"
      style={{ left: tooltip.x + 12, top: tooltip.y - 8 }}
    >
      {tooltip.label && (
        <div className="mb-1 font-medium text-foreground">{tooltip.label}</div>
      )}
      <div className="text-muted-foreground">{tooltip.description}</div>
    </div>,
    document.body
  );
};

// ── Hook: suppress Recharts tooltip while ref-dot is hovered ──

/**
 * Returns `true` while the user hovers a ReferenceDot that has a description.
 * Only sets up listeners when `enabled` is true (i.e. when dots with descriptions exist).
 */
export function useRefDotHovered(chartId: string, enabled: boolean): boolean {
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const onHover = (e: Event) => {
      if ((e as CustomEvent).detail.chartId === chartId) setHovered(true);
    };
    const onLeave = (e: Event) => {
      if ((e as CustomEvent).detail.chartId === chartId) setHovered(false);
    };
    window.addEventListener('chart-ref-dot-hover', onHover);
    window.addEventListener('chart-ref-dot-leave', onLeave);
    return () => {
      window.removeEventListener('chart-ref-dot-hover', onHover);
      window.removeEventListener('chart-ref-dot-leave', onLeave);
    };
  }, [chartId, enabled]);

  return hovered;
}

// ── Helper: does the dot array have any descriptions? ─────

export function hasReferenceDotDescriptions(
  items: ChartReferenceDot[] | undefined
): boolean {
  return !!items && items.some((d) => !!d.description);
}

// ── Y-value resolution for dots without explicit y ───────

/**
 * Returns a new array where every dot without an explicit `y` gets its value
 * resolved from the chart's data.  Looks up the data point whose `xAxisKey`
 * matches `dot.x` and picks the first numeric field that isn't the xAxisKey.
 *
 * Dots that already have `y` are passed through unchanged.
 * Dots whose `x` doesn't match any data point are dropped (nothing to snap to).
 */
export function resolveRefDotYValues(
  items: ChartReferenceDot[] | undefined,
  data: Record<string, any>[],
  xAxisKey: string,
): ChartReferenceDot[] | undefined {
  if (!items || items.length === 0) return items;

  // Fast path: every dot already has y — skip the lookup entirely
  if (items.every((d) => d.y !== undefined && d.y !== null)) return items;

  // Build a lookup: xValue → data row (first match wins)
  const byX = new Map<string | number, Record<string, any>>();
  for (const row of data) {
    const key = row[xAxisKey];
    if (key !== undefined && !byX.has(key)) byX.set(key, row);
  }

  return items.reduce<ChartReferenceDot[]>((acc, dot) => {
    if (dot.y !== undefined && dot.y !== null) {
      acc.push(dot);
      return acc;
    }

    const row = byX.get(dot.x);
    if (!row) return acc; // no matching data point — drop the dot

    // Pick the first numeric value that isn't the xAxisKey
    const yKey = Object.keys(row).find(
      (k) => k !== xAxisKey && typeof row[k] === 'number',
    );
    if (yKey === undefined) return acc;

    acc.push({ ...dot, y: row[yKey] });
    return acc;
  }, []);
}

// ── Render helpers ────────────────────────────────────────

/**
 * Renders an array of `ChartReferenceLine` as Recharts `<ReferenceLine>` elements.
 * Must be spread as children inside a Recharts chart component.
 */
export function renderReferenceLines(
  items: ChartReferenceLine[] | undefined,
  yAxisId?: string | number
): React.ReactNode[] {
  if (!items || items.length === 0) return [];

  return items.map((item, index) => (
    <ReferenceLine
      key={`ref-line-${index}-${item.x ?? item.y}`}
      x={item.x}
      y={item.y}
      yAxisId={yAxisId}
      stroke={item.color || DEFAULT_COLOR}
      strokeDasharray={item.strokeDasharray || DEFAULT_DASH}
      strokeWidth={1.5}
      label={
        item.label
          ? {
              value: clampLabel(item.label),
              position: 'insideTopRight' as const,
              fill: item.color || DEFAULT_COLOR,
              fontSize: 10,
              angle: item.x !== undefined ? -45 : 0,
              offset: 10,
            }
          : undefined
      }
    />
  ));
}

/**
 * Renders an array of `ChartReferenceDot` as raw Recharts `<ReferenceDot>` elements
 * with custom shapes (diamond, circle, square, triangle).
 *
 * For hover tooltips, pair this with `<ReferenceDotTooltipLayer />` rendered
 * as a sibling outside the Recharts chart.
 */
export function renderReferenceDots(
  items: ChartReferenceDot[] | undefined,
  chartId: string,
  yAxisId?: string | number
): React.ReactNode[] {
  if (!items || items.length === 0) return [];

  return items.map((dot, index) => {
    const color = dot.color || DEFAULT_COLOR;
    const r = dot.r ?? DEFAULT_DOT_RADIUS;

    return (
      <ReferenceDot
        key={`ref-dot-${index}-${dot.x}-${dot.y}`}
        x={dot.x}
        y={dot.y}
        yAxisId={yAxisId}
        r={r}
        fill={color}
        stroke="hsl(var(--background))"
        strokeWidth={2}
        shape={createDotShapeRenderer(dot, chartId)}
        isFront
        label={
          dot.label
            ? {
                value: clampLabel(dot.label),
                position: 'top' as const,
                fill: color,
                fontSize: 10,
                offset: r + 4,
              }
            : undefined
        }
      />
    );
  });
}

/**
 * Renders an array of `ChartReferenceArea` as Recharts `<ReferenceArea>` elements.
 * Must be spread as children inside a Recharts chart component.
 */
export function renderReferenceAreas(
  items: ChartReferenceArea[] | undefined,
  yAxisId?: string | number
): React.ReactNode[] {
  if (!items || items.length === 0) return [];

  return items.map((item, index) => (
    <ReferenceArea
      key={`ref-area-${index}-${item.x1}-${item.x2}`}
      x1={item.x1}
      x2={item.x2}
      y1={item.y1}
      y2={item.y2}
      yAxisId={yAxisId}
      fill={item.color || DEFAULT_COLOR}
      fillOpacity={item.fillOpacity ?? DEFAULT_AREA_OPACITY}
      stroke={item.color || DEFAULT_COLOR}
      strokeOpacity={0.3}
      label={
        item.label
          ? {
              value: item.label,
              position: 'insideTop' as const,
              fill: item.color || DEFAULT_COLOR,
              fontSize: 10,
            }
          : undefined
      }
    />
  ));
}
